/**
 * The notifications sheet's homes (docs/notifications/build.md). A button whose job is to
 * open it (the bell, the streak card) is its trigger, `NotifyTrigger`: the sheet's Radix root
 * sits round the button and the sheet opens inside it. The moments and Settings' row open it
 * from code through `openNotifySheet`, and the app mounts `NotifySheetHost` once for those.
 * The sheet itself loads lazily, on the first open.
 */
import { lazy, type ReactElement, Suspense, useEffect, useState } from 'react';

import { SheetRoot, SheetTrigger } from '../ui/index.ts';

import type { NotifySurface } from '../platform/analytics.ts';
import type { SheetGroup } from './state.ts';

export interface SheetRequest {
	surface: NotifySurface;
	/** The moment's own switch, first in the list. */
	first?: SheetGroup | null;
	/** The button it opened from: on a wide screen with a mouse it hangs under it (the bell). */
	anchor?: (() => HTMLElement | null) | undefined;
}

let opener: ((req: SheetRequest) => void) | null = null;

/** Opens the sheet (a no-op until the host is mounted). */
export function openNotifySheet(req: SheetRequest): void {
	opener?.(req);
}

const NotifySheet = lazy(() => import('./NotifySheet.tsx'));

export function NotifySheetHost() {
	const [req, setReq] = useState<(SheetRequest & { serial: number }) | null>(null);
	useEffect(() => {
		let serial = 0;
		opener = (r) => setReq({ ...r, serial: ++serial });
		return () => {
			opener = null;
		};
	}, []);
	if (!req) return null;
	return (
		<Suspense fallback={null}>
			<NotifySheet key={req.serial} surface={req.surface} first={req.first ?? null} anchor={req.anchor} onClose={() => setReq(null)} />
		</Suspense>
	);
}

/**
 * A button that opens the notifications sheet (the bell, the streak card): the sheet's Radix
 * trigger, so it says it opens a dialog and whether it's open, and focus comes back to it.
 * The button keeps its own look; pressing it opens the sheet with `req`.
 */
export function NotifyTrigger({ req, children }: { req: SheetRequest; children: ReactElement }) {
	const [serial, setSerial] = useState(0);
	const open = serial > 0;
	return (
		<SheetRoot open={open} onOpen={() => setSerial((n) => n + 1)}>
			<SheetTrigger>{children}</SheetTrigger>
			{open && (
				<Suspense fallback={null}>
					<NotifySheet key={serial} surface={req.surface} first={req.first ?? null} anchor={req.anchor} onClose={() => setSerial(0)} />
				</Suspense>
			)}
		</SheetRoot>
	);
}
